import { create } from 'zustand';
import { AppError, errorHandler } from '@/services/errors';

interface ErrorState {
  // Error storage
  errors: AppError[];
  toastErrors: AppError[];
  
  // Actions
  addError: (error: AppError) => void;
  removeError: (errorId: string) => void;
  clearErrors: (context?: string) => void;
  clearAllErrors: () => void;
  
  // Toast management
  showToast: (error: AppError) => void;
  dismissToast: (errorId: string) => void;
  clearToasts: () => void;
  
  // Error handling helpers
  handleError: (error: unknown, context?: string, showToast?: boolean) => AppError;
  showErrorMessage: (message: string, options?: {
    code?: string;
    context?: string;
    retryable?: boolean;
    showToast?: boolean;
  }) => AppError;
}

export const useErrorStore = create<ErrorState>((set, get) => ({
  errors: [],
  toastErrors: [],
  
  addError: (error: AppError) => {
    set((state) => ({
      errors: [...state.errors, error]
    }));
  },
  
  removeError: (errorId: string) => {
    set((state) => ({
      errors: state.errors.filter(error => error.id !== errorId),
      toastErrors: state.toastErrors.filter(error => error.id !== errorId)
    }));
  },
  
  clearErrors: (context?: string) => {
    set((state) => ({
      errors: context 
        ? state.errors.filter(error => error.context !== context)
        : [],
      toastErrors: context
        ? state.toastErrors.filter(error => error.context !== context)
        : state.toastErrors
    }));
  },
  
  clearAllErrors: () => {
    set({ errors: [], toastErrors: [] });
  },
  
  showToast: (error: AppError) => {
    set((state) => ({
      toastErrors: [...state.toastErrors, error]
    }));
  },
  
  dismissToast: (errorId: string) => {
    set((state) => ({
      toastErrors: state.toastErrors.filter(error => error.id !== errorId)
    }));
  },
  
  clearToasts: () => {
    set({ toastErrors: [] });
  },
  
  handleError: (error: unknown, context?: string, showToast = true) => {
    const appError = errorHandler.handleError(error, context);
    
    // Add to error store
    get().addError(appError);
    
    // Show toast if requested
    if (showToast) {
      get().showToast(appError);
    }
    
    return appError;
  },
  
  showErrorMessage: (message: string, options = {}) => {
    const appError = errorHandler.showError(message, {
      code: options.code as any,
      context: options.context,
      retryable: options.retryable
    });
    
    // Add to error store
    get().addError(appError);
    
    // Show toast if requested
    if (options.showToast !== false) {
      get().showToast(appError);
    }
    
    return appError;
  }
}));